/**
 * MaintenanceTable — Table component for the Maintenance Report.
 *
 * Supports dual column definitions for on-premise and cloud campuses.
 * Follows AlertDetailsTable.jsx patterns: COLUMN_DEFS, sort, filter,
 * and active filter bar. No kebab menu (no notes/reason editing).
 *
 * Props:
 *   data               — paginated rows for current page
 *   allData            — full filtered dataset (for filter unique values)
 *   visibleColumns     — string[] of visible column keys
 *   sortColumn         — current sort column key
 *   sortOrder          — 'asc' | 'desc'
 *   onSort             — (columnKey) => void
 *   filters            — { columnKey: [value1, ...] }
 *   onFilterChange     — (columnKey, selectedValues) => void
 *   onClearFilters     — () => void
 *   isCloud            — boolean (cloud vs on-premise)
 *   tzOffset           — timezone offset for datetime display (cloud only)
 */

import { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import { getDeviceProfileLink } from '../../../../utils/deviceLinks.js';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

// ---------------------------------------------------------------------------
// Column definitions — On-Premise
// ---------------------------------------------------------------------------

export const COLUMN_DEFS_ONPREMISE = [
    { key: 'EventDateTime', label: 'Event Date / Time', sortable: true, filterable: true, required: true },
    { key: '_duration', label: 'Duration', sortable: true, filterable: false },
    { key: 'AlertSubType', label: 'Category', sortable: true, filterable: true },
    { key: '_residents', label: 'Residents', sortable: false, filterable: false },
    { key: '_device_serial', label: 'Device / Serial #', sortable: false, filterable: false },
    { key: '_location', label: 'Location / Near', sortable: false, filterable: false },
];

// ---------------------------------------------------------------------------
// Column definitions — Cloud
// ---------------------------------------------------------------------------

export const COLUMN_DEFS_CLOUD = [
    { key: 'event_datetime_utc', label: 'Detected', sortable: true, filterable: true, required: true },
    { key: '_duration', label: 'Duration', sortable: true, filterable: false },
    { key: 'condition', label: 'Condition', sortable: true, filterable: true },
    { key: '_device_serial', label: 'Device / Serial', sortable: false, filterable: false },
    { key: 'area_name', label: 'Area', sortable: true, filterable: true },
    { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Compute elapsed duration from a datetime string to now.
 * Returns a human-readable string like "2h 15m" or "3d 5h".
 */
function computeDuration(dateStr) {
    if (!dateStr) return '\u2014';
    const then = new Date(dateStr);
    if (isNaN(then.getTime())) return '\u2014';
    const diffMs = Date.now() - then.getTime();
    if (diffMs < 0) return '\u2014';

    const totalMin = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
}

/**
 * Parse Residents JSON into an array of { fName, lName, id }.
 */
function parseResidents(row) {
    try {
        const data = typeof row.Residents === 'string' ? JSON.parse(row.Residents) : row.Residents;
        return Array.isArray(data) ? data : [];
    } catch {
        return [];
    }
}

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

function renderCellOnPremise(row, colKey) {
    switch (colKey) {
        case 'EventDateTime':
            return (
                <span className="dp-cell-text">
                    {row.EventDateTime || '\u2014'}
                </span>
            );

        case '_duration':
            return (
                <span className="dp-cell-text">
                    {computeDuration(row.EventDateTime)}
                </span>
            );

        case 'AlertSubType':
            return (
                <span className="dp-cell-text">
                    {row.AlertSubType || '\u2014'}
                </span>
            );

        case '_residents': {
            const residents = parseResidents(row);
            if (residents.length === 0) return <span className="dp-cell-text">{'\u2014'}</span>;
            return (
                <span className="dp-cell-text">
                    {residents.map((r, idx) => (
                        <span key={r.id || idx}>
                            {idx > 0 ? ', ' : ''}
                            <Link to={`/profiles/resident/${r.id}`} className="dp-link">
                                {`${r.fName || ''} ${r.lName || ''}`.trim() || '\u2014'}
                            </Link>
                        </span>
                    ))}
                </span>
            );
        }

        case '_device_serial':
            return (
                <div>
                    <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {row.DeviceUse || '\u2014'}
                    </span>
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.SerialNumber || ''}
                    </span>
                </div>
            );

        case '_location':
            return (
                <div>
                    <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {row.LocationName || '\u2014'}
                    </span>
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.LocationArea || ''}
                    </span>
                </div>
            );

        default:
            return <span className="dp-cell-text">{row[colKey] != null ? String(row[colKey]) : '\u2014'}</span>;
    }
}

function renderCellCloud(row, colKey, tzOffset) {
    switch (colKey) {
        case 'event_datetime_utc':
            return (
                <span className="dp-cell-text">
                    {row.event_datetime_utc ? convertIsoUTCToLocal(row.event_datetime_utc, tzOffset) : '\u2014'}
                </span>
            );

        case '_duration':
            return (
                <span className="dp-cell-text">
                    {computeDuration(row.event_datetime_utc)}
                </span>
            );

        case 'condition':
            return (
                <span className="dp-cell-text">
                    {row.condition || '\u2014'}
                </span>
            );

        case '_device_serial': {
            // Device rows: the shared rule (no link for repeaters, links wandering tags).
            const deviceLink = getDeviceProfileLink(row);
            return (
                <div>
                    {deviceLink ? (
                        <Link to={deviceLink} className="dp-link">
                            {row.device_name || '\u2014'}
                        </Link>
                    ) : (
                        <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-medium)' }}>
                            {row.device_name || '\u2014'}
                        </span>
                    )}
                    <br />
                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                        {row.manufacture_uuid || ''}
                    </span>
                </div>
            );
        }

        case 'area_name':
            return row.area_id ? (
                <Link to={`/profiles/area/${row.area_id}`} className="dp-link">
                    {row.area_name || '\u2014'}
                </Link>
            ) : (
                <span className="dp-cell-text">{row.area_name || '\u2014'}</span>
            );

        case 'resident_name':
            return row.resident_id ? (
                <Link to={`/profiles/resident/${row.resident_id}`} className="dp-link">
                    {row.resident_name || '\u2014'}
                </Link>
            ) : (
                <span className="dp-cell-text">{row.resident_name || '\u2014'}</span>
            );

        default:
            return <span className="dp-cell-text">{row[colKey] != null ? String(row[colKey]) : '\u2014'}</span>;
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MaintenanceTable = ({
    data,
    allData,
    visibleColumns,
    sortColumn,
    sortOrder,
    onSort,
    filters,
    onFilterChange,
    onClearFilters,
    isCloud,
    tzOffset,
}) => {
    const columnDefs = isCloud ? COLUMN_DEFS_CLOUD : COLUMN_DEFS_ONPREMISE;

    // Build column labels map for ActiveFilterBar
    const columnLabels = useMemo(() => {
        const labels = {};
        columnDefs.forEach((col) => { labels[col.key] = col.label; });
        return labels;
    }, [columnDefs]);

    // Build unique values for filterable columns from allData
    const filterValues = useMemo(() => {
        const vals = {};
        columnDefs.filter((c) => c.filterable).forEach((col) => {
            const unique = [...new Set((allData || []).map((row) => {
                const v = row[col.key];
                return v != null ? String(v) : '';
            }))].sort();
            vals[col.key] = unique;
        });
        return vals;
    }, [allData, columnDefs]);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        const current = filters[columnKey] || [];
        const updated = current.filter((v) => v !== value);
        onFilterChange(columnKey, updated);
    }, [filters, onFilterChange]);

    const visibleColDefs = columnDefs.filter((col) => visibleColumns.includes(col.key));

    const renderCell = isCloud
        ? (row, colKey) => renderCellCloud(row, colKey, tzOffset)
        : (row, colKey) => renderCellOnPremise(row, colKey);

    return (
        <div className="ap-table-container">
            {/* Active filter bar */}
            <ActiveFilterBar
                filters={filters}
                columnLabels={columnLabels}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={onClearFilters}
            />

            {/* Table */}
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            {visibleColDefs.map((col) => (
                                <th key={col.key} scope="col" className="ap-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                                    <div className="ap-th__content">
                                        {col.sortable ? (
                                            <button
                                                type="button"
                                                onClick={() => onSort(col.key)}
                                                className="ap-th__sort-btn"
                                                aria-label={`Sort by ${col.label}`}
                                            >
                                                {col.label}
                                                {sortColumn === col.key ? (
                                                    <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                        {sortOrder === 'asc'
                                                            ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                            : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                        }
                                                    </svg>
                                                ) : null}
                                            </button>
                                        ) : (
                                            <span>{col.label}</span>
                                        )}

                                        {col.filterable ? (
                                            <FilterPopover
                                                columnKey={col.key}
                                                label={col.label}
                                                values={filterValues[col.key] || []}
                                                selectedValues={filters[col.key] || []}
                                                onChange={onFilterChange}
                                            />
                                        ) : null}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr className="dp-tr">
                                <td className="dp-td dp-td--center" colSpan={visibleColDefs.length} style={{ padding: 'var(--space-8)' }}>
                                    <span className="dp-cell-text dp-cell-text--muted">No events found.</span>
                                </td>
                            </tr>
                        ) : null}
                        {data.map((row, idx) => (
                            <tr key={row.event_id || row.EventId || idx} className="dp-tr">
                                {visibleColDefs.map((col) => (
                                    <td key={col.key} className="dp-td">
                                        {renderCell(row, col.key)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MaintenanceTable;
